import axios from "axios";
import { validateClinicBranchParams } from "@/utils/routeValidation";

// Empty date inputs mean "no bound"; leave them out so the API sees an open range.
const dateParams = ({ payer, dateFrom, dateTo, dateBasis }) => ({
  payer,
  ...(dateFrom ? { dateFrom } : {}),
  ...(dateTo ? { dateTo } : {}),
  ...(dateBasis ? { dateBasis } : {}),
});

const moduleReconciliation = {
  namespaced: true,
  state: {
    outstanding: [],
    paid: [],
    // { pagination: {offset, limit, count, total}, summary: {totalAmount} } of the last list fetch
    outstandingMeta: null,
    paidMeta: null,
    needsReview: [],
  },
  mutations: {
    // `box` is the per-row tick used by OutstandingTab's select column. It MUST be
    // declared here, at object-creation time, so Vue 2's Observer walks it and makes
    // it reactive. If rows reached the checkbox without it, `v-model="row.item.box"`
    // would be a plain assignment on an already-observed object — no dep, so
    // selectedIds/isAllChecked would silently stop updating. Any caller may refetch
    // outstanding (e.g. NeedsReviewTab's match picker) while OutstandingTab is still
    // mounted, so the guarantee belongs in the mutation, not in one consumer's load().
    setOutstanding(state, items) {
      state.outstanding = (items || []).map((i) => ({ ...i, box: false }));
    },
    setPaid(state, items) {
      state.paid = items;
    },
    setOutstandingMeta(state, meta) {
      state.outstandingMeta = meta;
    },
    setPaidMeta(state, meta) {
      state.paidMeta = meta;
    },
    setNeedsReview(state, items) {
      state.needsReview = items;
    },
  },
  actions: {
    // keepList: fetch for a caller that keeps its own copy (the match picker) without
    // replacing the date-filtered list the ค้างรับ tab is showing. includeBeforeStart also lists
    // claims served before reconciliation went live (not owed, but still reconcilable).
    async fetchOutstanding({ commit }, { clinicUrl, branchUrl, payer, dateFrom, dateTo, includeBeforeStart = false, keepList = false }) {
      if (!validateClinicBranchParams(clinicUrl, branchUrl, "fetchOutstanding")) {
        return Promise.reject(new Error("Invalid clinic/branch parameters"));
      }
      try {
        const res = await axios.get(
          `/api/reconciliation/outstanding/${clinicUrl}/${branchUrl}`,
          {
            params: { ...dateParams({ payer, dateFrom, dateTo }), ...(includeBeforeStart ? { includeBeforeStart: true } : {}) },
            skipGlobalErrorHandler: true,
          }
        );
        const items = Array.isArray(res.data) ? res.data : (res.data.claims || []);
        if (!keepList) {
          commit("setOutstanding", items);
          commit("setOutstandingMeta", {
            pagination: res.data.pagination || null,
            summary: res.data.summary || null,
            startDate: res.data.startDate || null,
          });
        }
        return res;
      } catch (err) {
        console.error("fetchOutstanding failed", err);
        throw err;
      }
    },
    async fetchPaid({ commit }, { clinicUrl, branchUrl, payer, dateFrom, dateTo, dateBasis }) {
      if (!validateClinicBranchParams(clinicUrl, branchUrl, "fetchPaid")) {
        return Promise.reject(new Error("Invalid clinic/branch parameters"));
      }
      try {
        const res = await axios.get(
          `/api/reconciliation/paid/${clinicUrl}/${branchUrl}`,
          { params: dateParams({ payer, dateFrom, dateTo, dateBasis }), skipGlobalErrorHandler: true }
        );
        const items = Array.isArray(res.data) ? res.data : (res.data.claims || []);
        commit("setPaid", items);
        commit("setPaidMeta", { pagination: res.data.pagination || null, summary: res.data.summary || null });
        return res;
      } catch (err) {
        console.error("fetchPaid failed", err);
        throw err;
      }
    },
    // รอตรวจสอบ: every unresolved statement line of the payer, across all live imports
    async fetchUnmatched({ commit }, { clinicUrl, branchUrl, payer, dateFrom, dateTo }) {
      if (!validateClinicBranchParams(clinicUrl, branchUrl, "fetchUnmatched")) {
        return Promise.reject(new Error("Invalid clinic/branch parameters"));
      }
      try {
        const res = await axios.get(
          `/api/reconciliation/unmatched/${clinicUrl}/${branchUrl}`,
          { params: dateParams({ payer, dateFrom, dateTo }), skipGlobalErrorHandler: true }
        );
        commit("setNeedsReview", Array.isArray(res.data) ? res.data : []);
        return res;
      } catch (err) {
        console.error("fetchUnmatched failed", err);
        throw err;
      }
    },
    // lines staff closed without a match (IGNORED), with their reason — kept by the caller
    async fetchClosedLines(_, { clinicUrl, branchUrl, payer }) {
      if (!validateClinicBranchParams(clinicUrl, branchUrl, "fetchClosedLines")) {
        return Promise.reject(new Error("Invalid clinic/branch parameters"));
      }
      const res = await axios.get(`/api/reconciliation/unmatched/${clinicUrl}/${branchUrl}`, {
        params: { payer, status: "IGNORED" },
        skipGlobalErrorHandler: true,
      });
      return Array.isArray(res.data) ? res.data : [];
    },
    // ลูกหนี้ สปสช./ปกส. report: per payer, claims owed at the end of `asOf` aged into buckets
    async fetchAging(_, { clinicUrl, branchUrl, asOf }) {
      if (!validateClinicBranchParams(clinicUrl, branchUrl, "fetchAging")) {
        return Promise.reject(new Error("Invalid clinic/branch parameters"));
      }
      const res = await axios.get(`/api/reconciliation/aging/${clinicUrl}/${branchUrl}`, {
        params: { asOf },
        skipGlobalErrorHandler: true,
      });
      return res.data;
    },
    // The three status tiles: count + amount per status for the panel's filter. limit=0 makes the
    // list endpoints return only their totals; unmatched lines are few, so they are summed here.
    async fetchTotals(_, { clinicUrl, branchUrl, payer, dateFrom, dateTo, dateBasis }) {
      if (!validateClinicBranchParams(clinicUrl, branchUrl, "fetchTotals")) {
        return Promise.reject(new Error("Invalid clinic/branch parameters"));
      }
      const base = `/api/reconciliation`;
      const opts = (params) => ({ params: { ...params, limit: 0 }, skipGlobalErrorHandler: true });
      const [out, paid, review] = await Promise.all([
        axios.get(`${base}/outstanding/${clinicUrl}/${branchUrl}`, opts(dateParams({ payer, dateFrom, dateTo }))),
        axios.get(`${base}/paid/${clinicUrl}/${branchUrl}`, opts(dateParams({ payer, dateFrom, dateTo, dateBasis }))),
        axios.get(`${base}/unmatched/${clinicUrl}/${branchUrl}`, { params: dateParams({ payer, dateFrom, dateTo }), skipGlobalErrorHandler: true }),
      ]);
      const total = (d) => ({ count: Number(d.pagination?.total || 0), amount: Number(d.summary?.totalAmount || 0) });
      const lines = Array.isArray(review.data) ? review.data : [];
      return {
        // the day ค้างรับ counts from (reconciliation go-live), for the ทั้งหมด chip
        startDate: out.data.startDate || null,
        outstanding: total(out.data),
        paid: total(paid.data),
        review: { count: lines.length, amount: lines.reduce((sum, l) => sum + Number(l.amount || 0), 0) },
      };
    },
    markPaid(_, { clinicUrl, branchUrl, payload }) {
      if (!validateClinicBranchParams(clinicUrl, branchUrl, "markPaid")) {
        return Promise.reject(new Error("Invalid clinic/branch parameters"));
      }
      return axios
        .post(
          `/api/reconciliation/mark-paid/${clinicUrl}/${branchUrl}`,
          payload,
          { skipGlobalErrorHandler: true }
        )
        .then(res => res)
        .catch(err => { throw err; });
    },
    manualMatch(_, { clinicUrl, branchUrl, lineId, claimId }) {
      if (!validateClinicBranchParams(clinicUrl, branchUrl, "manualMatch")) {
        return Promise.reject(new Error("Invalid clinic/branch parameters"));
      }
      return axios
        .post(
          `/api/reconciliation/manual-match/${clinicUrl}/${branchUrl}`,
          { lineId, claimId },
          { skipGlobalErrorHandler: true }
        )
        .then(res => res)
        .catch(err => { throw err; });
    },
    // one line: unmatch (back to รอตรวจสอบ), ignore (close with a reason) or reopen
    updateLineStatus(_, { clinicUrl, branchUrl, lineId, action, note = null }) {
      if (!validateClinicBranchParams(clinicUrl, branchUrl, "updateLineStatus")) {
        return Promise.reject(new Error("Invalid clinic/branch parameters"));
      }
      return axios.post(
        `/api/reconciliation/line-status/${clinicUrl}/${branchUrl}`,
        { lineId, action, ...(note ? { note } : {}) },
        { skipGlobalErrorHandler: true }
      );
    },
    undoBatch(_, { clinicUrl, branchUrl, batchId }) {
      if (!validateClinicBranchParams(clinicUrl, branchUrl, "undoBatch")) {
        return Promise.reject(new Error("Invalid clinic/branch parameters"));
      }
      return axios
        .post(
          `/api/reconciliation/undo-batch/${clinicUrl}/${branchUrl}`,
          { batchId },
          { skipGlobalErrorHandler: true }
        )
        .then(res => res)
        .catch(err => { throw err; });
    },
    uploadStatement(_, { clinicUrl, branchUrl, formData }) {
      if (!validateClinicBranchParams(clinicUrl, branchUrl, "uploadStatement")) {
        return Promise.reject(new Error("Invalid clinic/branch parameters"));
      }
      return axios
        .post(
          `/api/reconciliation/upload/${clinicUrl}/${branchUrl}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            skipGlobalErrorHandler: true,
          }
        )
        .then(res => res)
        .catch(err => { throw err; });
    },
    ocrExtract(_, { clinicUrl, branchUrl, formData }) {
      if (!validateClinicBranchParams(clinicUrl, branchUrl, "ocrExtract")) {
        return Promise.reject(new Error("Invalid clinic/branch parameters"));
      }
      return axios
        .post(
          `/api/reconciliation/ocr-extract/${clinicUrl}/${branchUrl}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            skipGlobalErrorHandler: true,
          }
        )
        .then(res => res)
        .catch(err => { throw err; });
    },
    commitReviewed(_, { clinicUrl, branchUrl, payload }) {
      if (!validateClinicBranchParams(clinicUrl, branchUrl, "commitReviewed")) {
        return Promise.reject(new Error("Invalid clinic/branch parameters"));
      }
      return axios
        .post(
          `/api/reconciliation/commit-reviewed/${clinicUrl}/${branchUrl}`,
          payload,
          { skipGlobalErrorHandler: true }
        )
        .then(res => res)
        .catch(err => { throw err; });
    },
  },
  getters: {
    getOutstanding: (state) => state.outstanding,
    getPaid: (state) => state.paid,
    getOutstandingMeta: (state) => state.outstandingMeta,
    getPaidMeta: (state) => state.paidMeta,
    getNeedsReview: (state) => state.needsReview,
  },
};

export default moduleReconciliation;
